import {
  applyOperation,
  type ConnectionOperation,
  isEntityOperation,
  type PendingConnectionOperation,
} from '@github-ui/optimistic-connection'
import {
  CONNECTION_MUTATION_KEY_ROOT,
  type ConnectionMutationVariables,
  connectionTargetKey,
  usePendingOperationEntries,
} from '@github-ui/optimistic-connection/pendingOperations'
import {registerSettlementHandler} from '@github-ui/optimistic-connection/settlementRegistry'
import type {IssueViewerQueryIdentifier} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useQueryClient} from '@github-ui/react-query'
import {useEffect, useEffectEvent, useMemo, useState} from 'react'

import {reconcileCanonicalTimelineSnapshot} from './fetch-canonical-timeline-snapshot'
import {TIMELINE_CONNECTION_PATH, type TimelineFeed, type TimelineIssue} from './fetch-timeline-items'
import {correlationKeysOf, ownCountDelta, readsInclude, readsIncludeAdd, withCountDelta} from './timeline-write-overlay'

/**
 * # Showing a comment before the server has confirmed it
 *
 * A comment the reader posts, edits or deletes has to be on screen before the server has answered,
 * and must not flicker when it does. This is the half of the timeline that arranges that; the
 * sections it applies to, and the reading of them, live in `use-timeline-items-tanstack.ts`.
 *
 * **1. The overlay shows it.** The mutation dispatches an operation, which stays pending in the
 * mutation cache until the server answers. The hook applies whatever is pending over the rows it
 * reads, so the row renders from the operation rather than from any cache entry.
 *
 * Which sections an operation reaches is the caller's decision, and this returns the two groups it
 * needs: an added row belongs at the end of the timeline, so it goes to one section only, while a
 * delete or an edit names a row that could be anywhere and goes to all of them.
 *
 * **2. The write settles into the cache.** When the mutation succeeds, the server's row is written
 * into the cache, so it survives the overlay being dropped.
 *
 * **3. The overlay drops.** The operation stops being pending and the row is now the cache's.
 */

const NO_OPERATIONS: PendingConnectionOperation[] = []

export type TimelineWrites = {
  /** For the one section holding the end of the timeline, where an added row belongs. */
  endSectionOperations: PendingConnectionOperation[]
  /** For every other section: a row a delete or an edit names can be anywhere. */
  everySectionOperations: PendingConnectionOperation[]
  /** The correlation key a settled write left on a row, by the server id it came back as. */
  correlationKeys: ReadonlyMap<string, string>
}

export function useTimelineWrites({
  issueId,
  coordinates,
  primaryIdentifier,
  primaryQueryKey,
  feed = 'activity',
  endSectionQueryKey,
  timelineKeyPrefix,
  inactiveTimelineKeyPrefix,
  reads,
}: {
  issueId: string
  coordinates: {owner: string; repo: string; number: number}
  primaryIdentifier: IssueViewerQueryIdentifier
  primaryQueryKey?: readonly unknown[]
  feed?: TimelineFeed
  /** The cache entry an added row is written into, and the one the timeline's total is read from. */
  endSectionQueryKey: readonly unknown[]
  timelineKeyPrefix: readonly unknown[]
  /** A loaded inactive feed is marked stale after writes, but never fetched in the background. */
  inactiveTimelineKeyPrefix?: readonly unknown[]
  /** What the sections have read, which is what says whether a write has been taken up yet. */
  reads: Array<TimelineIssue | undefined>
}): TimelineWrites {
  const queryClient = useQueryClient()
  const enabled = Boolean(issueId)
  const timelineTarget = connectionTargetKey(issueId, TIMELINE_CONNECTION_PATH)

  const pendingEntries = usePendingOperationEntries()

  // Structural writes are scoped to this connection. Entity writes are global, so take only the
  // ones targeting a comment held by one of this timeline's windows.
  const pendingOperations = useMemo(
    () =>
      enabled
        ? pendingEntries.flatMap<PendingConnectionOperation>(entry =>
            entry != null &&
            (isEntityOperation(entry.operation)
              ? readsInclude(reads, entry.operation.id)
              : connectionTargetKey(entry.ownerId, entry.connectionPath) === timelineTarget)
              ? [entry]
              : [],
          )
        : NO_OPERATIONS,
    [enabled, pendingEntries, reads, timelineTarget],
  )

  // Lives as long as the rows it names are on screen, which is what a stable React key has to
  // outlast. Every add that settles here leaves one, and leaving the issue drops them all.
  const [correlationKeys, setCorrelationKeys] = useState<ReadonlyMap<string, string>>(() => new Map())

  /**
   * The server has confirmed a write, so its result is written straight into the cache. That is
   * what "settling" means here, and it is deliberately not a refetch: writing the row directly
   * means the cache already holds it at the moment the pending operation standing in for it drops,
   * later in this same tick. Refetching would spread the write, the response and the drop across
   * three ticks, and the reader sees that as a jump.
   */
  const settle = useEffectEvent(async (operation: ConnectionOperation) => {
    const settled = correlationKeysOf(operation)
    if (settled.length > 0) setCorrelationKeys(known => new Map([...known, ...settled]))

    // A read already in flight would land after this write and undo it, and a read issued this
    // close to a write can come back from a replica that does not hold the row yet. Cancel as far
    // as the write reaches: an add lands in one section, a delete or an edit in all of them.
    await queryClient.cancelQueries({
      queryKey: operation.type === 'addToConnection' ? endSectionQueryKey : timelineKeyPrefix,
    })

    if (operation.type === 'addToConnection') {
      queryClient.setQueryData<TimelineIssue>(endSectionQueryKey, cached =>
        cached ? applyOperation(cached, issueId, operation) : cached,
      )
      if (inactiveTimelineKeyPrefix) {
        void queryClient.invalidateQueries({queryKey: inactiveTimelineKeyPrefix, refetchType: 'none'})
      }
      if (feed === 'comments') {
        await queryClient.invalidateQueries({queryKey: endSectionQueryKey})
      } else {
        await reconcileCanonicalTimelineSnapshot({
          coordinates,
          primaryIdentifier,
          issueId,
          primaryQueryKey,
          queryClient,
        })
      }
      return
    }

    const countBefore = queryClient.getQueryData<TimelineIssue>(endSectionQueryKey)?.timelineItems?.totalCount

    // Every section, or the row comes back wherever this did not reach.
    queryClient.setQueriesData<TimelineIssue>({queryKey: timelineKeyPrefix}, cached =>
      cached ? applyOperation(cached, issueId, operation) : cached,
    )

    // `applyOperation` moves the count only for a section that held the row, and the section the
    // total is read from need not be one of them. Left alone, the total springs back as the pending
    // delta drops, and the marker counts *up* after a delete.
    //
    // Only while the reads still hold the row, though: once they have taken it, they have taken its
    // count with it, and the pending delta has already stopped counting for the same reason. Moving
    // the count again here would take it twice.
    const delta = ownCountDelta(operation)
    const countAfter = queryClient.getQueryData<TimelineIssue>(endSectionQueryKey)?.timelineItems?.totalCount
    const stillRead = operation.type !== 'removeFromConnection' || readsInclude(reads, operation.nodeId)
    if (stillRead && delta !== 0 && countBefore !== undefined && countAfter === countBefore) {
      queryClient.setQueryData<TimelineIssue>(endSectionQueryKey, cached =>
        cached ? withCountDelta(cached, delta) : cached,
      )
    }
    if (inactiveTimelineKeyPrefix) {
      void queryClient.invalidateQueries({queryKey: inactiveTimelineKeyPrefix, refetchType: 'none'})
    }

    if (operation.type === 'removeFromConnection') {
      if (feed === 'comments') {
        await queryClient.invalidateQueries({queryKey: endSectionQueryKey})
      } else {
        await reconcileCanonicalTimelineSnapshot({
          coordinates,
          primaryIdentifier,
          issueId,
          primaryQueryKey,
          queryClient,
        })
      }
    }
  })

  /**
   * A write still in flight when this registration ends confirms into a registry that no longer
   * holds it, so the cache is never reconciled and the overlay drops onto sections that never
   * received the write. Refetch instead of leaving them stale.
   *
   * Both the target and the cache prefix are the ones this registration served. The hook can be
   * re-rendered with a different issue rather than unmounted, and reading either from the current
   * render would then name the issue being entered and leave the one being left stale.
   */
  const cachedTimelineIncludes = useEffectEvent((queryKey: readonly unknown[], entityId: string) =>
    queryClient
      .getQueriesData<TimelineIssue>({queryKey})
      .some(([, cached]) => cached !== undefined && readsInclude([cached], entityId)),
  )

  const abandonOutstanding = useEffectEvent((outgoing: string, outgoingKeyPrefix: readonly unknown[]) => {
    const mine = queryClient
      .getMutationCache()
      .findAll({status: 'pending', mutationKey: [CONNECTION_MUTATION_KEY_ROOT]})
      .some(mutation => {
        const operation = (mutation.state.variables as ConnectionMutationVariables | undefined)?.operation
        if (!operation) return false
        if (isEntityOperation(operation)) {
          return cachedTimelineIncludes(outgoingKeyPrefix, operation.id)
        }

        const key = mutation.options.mutationKey
        if (!Array.isArray(key)) return false
        const path = Array.isArray(key[2]) ? (key[2] as string[]) : []
        return connectionTargetKey(String(key[1]), path) === outgoing
      })
    if (mine) void queryClient.invalidateQueries({queryKey: outgoingKeyPrefix})
  })

  // Registered directly rather than through `useOptimisticData`, which writes to one document.
  // These sections each cover a different slice, and a write belongs to exactly one of them.
  useEffect(() => {
    if (!enabled) return
    const registeredFor = timelineTarget
    const registeredPrefix = timelineKeyPrefix
    const handler = async (operation: ConnectionOperation, operationOwnerId: string, connectionPath: string[]) => {
      const belongsHere = isEntityOperation(operation)
        ? cachedTimelineIncludes(registeredPrefix, operation.id)
        : connectionTargetKey(operationOwnerId, connectionPath) === registeredFor
      if (!belongsHere) return
      await settle(operation)
    }
    const unregister = registerSettlementHandler(handler)
    return () => {
      unregister()
      abandonOutstanding(registeredFor, registeredPrefix)
    }
  }, [enabled, timelineTarget, timelineKeyPrefix])

  // An add stops applying once its own row has arrived, or the comment is rendered twice and
  // counted twice.
  const endSectionOperations = useMemo(
    () =>
      pendingOperations.filter(
        entry => entry.operation.type !== 'addToConnection' || !readsIncludeAdd(reads, entry.operation),
      ),
    [pendingOperations, reads],
  )

  const everySectionOperations = useMemo(
    () => endSectionOperations.filter(entry => entry.operation.type !== 'addToConnection'),
    [endSectionOperations],
  )

  return {endSectionOperations, everySectionOperations, correlationKeys}
}
